import Modal from './Modal';
import { ModalSize } from './Modal.types';
import { CalendarIcon, CheckCircleIcon } from '../icons';
import ClipBoardIcon from '../icons/ClipBoardIcon';
import { Button, ButtonType, IconName } from '@hills-design-system/components';
import { useShelterState } from '../../hooks/useShelter';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';

interface PetszelLearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PetszelLearnMoreModal = ({ isOpen, onClose }: PetszelLearnMoreModalProps) => {
  const { shelter } = useShelterState();
  const { t } = useTranslation();

  const cards = [
    {
      icon: <CalendarIcon className="text-primary" />,
      text: t(TranslationKeyEnum.PetszelDescription1),
    },
    {
      icon: <ClipBoardIcon className="text-primary" />,
      text: t(TranslationKeyEnum.PetszelDescription2),
    },
    {
      icon: <CheckCircleIcon className="text-primary" />,
      text: t(TranslationKeyEnum.PetszelDescription3),
    },
  ];

  const steps = [
    {
      title: t(TranslationKeyEnum.ProcessStep1Title),
      description: (
        <>
          {t(TranslationKeyEnum.ProcessStep1Description)}{' '}
          <span className="text-primary">
            {t(TranslationKeyEnum.HillsAccountNumber, shelter?.sold_to ?? '')}
          </span>
        </>
      ),
    },
    {
      title: t(TranslationKeyEnum.ProcessStep2Title),
      description: t(TranslationKeyEnum.ProcessStep2Description),
    },
    {
      title: t(TranslationKeyEnum.ProcessStep3Title),
      description: t(TranslationKeyEnum.ProcessStep3Description),
    },
  ];

  const navigateToPetszel = () => {
    window.open('https://quo9g1jgnm6.typeform.com/to/SmgBB7ij', '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t(TranslationKeyEnum.AutomateHillsReporting)}
      size={ModalSize.Large}
      eyebrow={t(TranslationKeyEnum.NewLabel)}
      className="font-sans"
      eyebrowClassName="bg-[#49A338] text-white font-bold px-3 py-0.5 rounded-2xl mb-3"
    >
      <div className="flex flex-col gap-4 max-w-4xl mx-auto">
        {cards.map((card, index) => (
          <div key={index} className="flex items-start gap-4 p-8 bg-[#EDF2F8] rounded-[16px]">
            {/* Icon Container */}
            <div className="mt-1 flex-shrink-0">{card.icon}</div>

            {/* Content */}
            <p className="text-base leading-relaxed text-black font-medium">{card.text}</p>
          </div>
        ))}
      </div>
      <div className="max-w-4xl mx-auto mt-8">
        {/* Section Title */}
        <h2 className="text-2xl font-extrabold mb-6 px-2">{t(TranslationKeyEnum.HowProcessWork)}</h2>

        {/* Main Container */}
        <div className="bg-[#EDF2F8] rounded-[24px] p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col gap-4">
                {/* Large Blue Number */}
                <span className="text-4xl font-black text-primary">{index + 1}.</span>

                {/* Step Title */}
                <h3 className="text-lg font-bold text-[#1a1a1a]">{step.title}</h3>

                {/* Step Description */}
                <p className="text-base font-medium text-black ">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Fixed Bottom Bar */}
      <div className="sticky bottom-[-32px] left-0 right-0 -mx-8 z-60 bg-white py-200 px-200">
        <div className="flex justify-end gap-150">
          <Button
            label={t(TranslationKeyEnum.ConnectUsingPetszel)}
            type={ButtonType.Primary}
            icon={IconName.ExternalLink}
            onClick={() => navigateToPetszel()}
          />
        </div>
      </div>
    </Modal>
  );
};

export default PetszelLearnMoreModal;
